import * as amplitude from "@amplitude/analytics-browser";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo } from "react";
import { RiArrowDownSLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { FILTER_DELIMITER, SearchAndFilterTypes } from "src/constants";
import { localStorageRecord } from "src/services/localStorage";
import { z } from "zod";

const COLUMN_SELECTION_KEY = "visible_columns";

type Column = {
  id: string;
  header: string;
};

type ViewColumnSelectorProps = {
  allColumns: Column[];
  visibleColumns: string[];
  onToggleColumn: (columnId: string) => void;
  showAdvancedSettingsOnly?: boolean;
  advancedSettings?: React.ReactNode;
};

export const ViewColumnSelector: React.FC<ViewColumnSelectorProps> = ({
  allColumns,
  visibleColumns,
  onToggleColumn,
  showAdvancedSettingsOnly,
  advancedSettings,
}) => {
  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={Button}
        variant="banner"
        size="sm"
        onClick={() => {
          amplitude.track("Clicked View columns");
        }}
        rightIcon={<RiArrowDownSLine />}
      >
        View columns
      </MenuButton>

      <MenuList maxH="420px" overflowY="auto" zIndex="popover">
        {/* If showAdvancedSettingsOnly is true and advancedSettings is provided, don't show the column menu options */}
        {!(showAdvancedSettingsOnly && advancedSettings) &&
          allColumns
            .filter((column) => column.header)
            .map((column) => (
              <MenuItem
                key={column.id}
                onClick={() => onToggleColumn(column.id)}
                closeOnSelect={false}
                gap={2}
              >
                {/* Checkbox click is inconsistent (e.g. when focused), so we use onChange + wrapper instead. */}
                <Box
                  as="span"
                  display="contents"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Checkbox
                    isChecked={visibleColumns.includes(column.id)}
                    onChange={() => onToggleColumn(column.id)}
                  />
                </Box>
                {column.header}
              </MenuItem>
            ))}

        {advancedSettings && (
          <>
            {!showAdvancedSettingsOnly && <Divider />}
            {advancedSettings}
          </>
        )}
      </MenuList>
    </Menu>
  );
};

type UseColumnSelectionProps = {
  allColumns: Column[];
  requiredColumnIds?: string[];
  resourceName?: string;
  showAdvancedSettingsOnly?: boolean;
  advancedSettings?: React.ReactNode;
  columnVisibilityPrefixesToExpand?: string[];
};

export function useColumnSelection({
  allColumns,
  requiredColumnIds = [],
  resourceName,
  showAdvancedSettingsOnly,
  advancedSettings,
  columnVisibilityPrefixesToExpand = [],
}: UseColumnSelectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const allColumnIds = useMemo(
    () => allColumns.map((col) => col.id),
    [allColumns]
  );

  const storedColumns = useMemo(
    () =>
      resourceName
        ? localStorageRecord(
            `${COLUMN_SELECTION_KEY}_${resourceName}`,
            z.array(z.string())
          )
        : null,
    [resourceName]
  );

  const convertSearchParamsToVisibleColumns = useCallback(() => {
    const visibleColumnsParam = searchParams.get(
      SearchAndFilterTypes.VisibleColumns
    );
    return [
      ...requiredColumnIds, // Required columns are always visible and are not included in the visibleColumns URL parameter value.
      ...(visibleColumnsParam?.split(FILTER_DELIMITER) ?? []),
    ];
  }, [requiredColumnIds, searchParams]);

  const visibleColumns = useMemo(() => {
    return convertSearchParamsToVisibleColumns() ?? allColumnIds;
  }, [allColumnIds, convertSearchParamsToVisibleColumns]);

  const convertVisibleColumnsToSearchParams = useCallback(
    (columns: string[]) => {
      return columns
        .filter((id) => !requiredColumnIds.includes(id)) // Required columns are always visible and are not included in the visibleColumns URL parameter value.
        .join(FILTER_DELIMITER);
    },
    [requiredColumnIds]
  );

  // Restore search params from stored columns or default columns on initial load
  useEffect(() => {
    const visibleColumnsParam = searchParams.get(
      SearchAndFilterTypes.VisibleColumns
    );
    if (!visibleColumnsParam) {
      const storedColumnsValue = storedColumns?.getValue();
      const columns = storedColumnsValue ?? allColumnIds;

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(
        SearchAndFilterTypes.VisibleColumns,
        convertVisibleColumnsToSearchParams(columns)
      );
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storedColumns,
    allColumnIds,
    convertVisibleColumnsToSearchParams,
    setSearchParams,
  ]); // Do not include searchParams to avoid race conditions

  // Save visible columns to local storage when they change, when not in a saved view.
  useEffect(() => {
    if (
      storedColumns &&
      !searchParams.get(SearchAndFilterTypes.SavedView) &&
      searchParams.get(SearchAndFilterTypes.VisibleColumns)
    ) {
      // Use URL search params as source of truth, not visibleColumns value, to avoid race conditions
      storedColumns.setValue(convertSearchParamsToVisibleColumns());
    }
  }, [searchParams, storedColumns, convertSearchParamsToVisibleColumns]);

  // Note for CQT:
  // The URL search params (which converts to visibleColumns) include one `question-<id>` per CQT.
  // The table columns (allColumns) include one column per nested question, in the format `question-<id>-<nested-question-id>`.
  // See below for how the "expansion" logic works.
  const columnVisibility = useMemo(() => {
    const visibility = Object.fromEntries(
      allColumns.map((column) => [column.id, false])
    );
    for (const visibleColumn of visibleColumns) {
      // If visibleColumn starts with a prefix in columnVisibilityPrefixesToExpand,
      // include all columns that start with visibleColumn's value.
      // This is to support CQT where one visibleColumn maps to multiple table columns, e.g.:
      // columnVisibilityPrefixesToExpand = ["question-"]
      // visibleColumn = `question-${customQuestionId}`
      // Each column for nested field `question-${customQuestionId}-${nestedQuestionId}` should be visible.
      if (
        columnVisibilityPrefixesToExpand.some((prefix) =>
          visibleColumn.startsWith(prefix)
        )
      ) {
        allColumns.forEach((column) => {
          if (column.id.startsWith(visibleColumn)) {
            visibility[column.id] = true;
          }
        });
      } else if (allColumns.some((column) => column.id === visibleColumn)) {
        visibility[visibleColumn] = true;
      }
    }
    return visibility;
  }, [allColumns, visibleColumns, columnVisibilityPrefixesToExpand]);

  const toggleColumnSelection = useCallback(
    (columnId: string) => {
      const newVisibleColumns = visibleColumns.includes(columnId)
        ? visibleColumns.filter((id) => id !== columnId)
        : // Preserve the original order of the columns.
          allColumnIds.filter(
            (id) => visibleColumns.includes(id) || id === columnId
          );

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(
        SearchAndFilterTypes.VisibleColumns,
        convertVisibleColumnsToSearchParams(newVisibleColumns)
      );

      // If the column is being removed and it is the current sort key, remove the sort key.
      if (
        visibleColumns.includes(columnId) &&
        searchParams.get(SearchAndFilterTypes.SortKey) === columnId
      ) {
        newSearchParams.delete(SearchAndFilterTypes.SortKey);
      }

      setSearchParams(newSearchParams, { replace: true });

      amplitude.track("Changed column in view", {
        view_column_name:
          allColumns.find((column) => column.id === columnId)?.header ??
          columnId,
        active_column: newVisibleColumns.includes(columnId)
          ? "is_active"
          : "is_inactive",
        checkbox_location: "default_columns",
      });
    },
    [
      allColumnIds,
      allColumns,
      convertVisibleColumnsToSearchParams,
      searchParams,
      setSearchParams,
      visibleColumns,
    ]
  );

  return {
    visibleColumns,
    columnVisibility,
    toggleColumnSelection,
    ViewColumnSelector: () => (
      // Only allow toggling columns that are not required.
      <ViewColumnSelector
        allColumns={allColumns.filter(
          (col) => !requiredColumnIds.includes(col.id)
        )}
        visibleColumns={visibleColumns.filter(
          (id) => !requiredColumnIds.includes(id)
        )}
        onToggleColumn={toggleColumnSelection}
        showAdvancedSettingsOnly={showAdvancedSettingsOnly}
        advancedSettings={advancedSettings}
      />
    ),
  };
}
